import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,  // Adicionar
  CardActions,  // Adicionar
  CircularProgress, // Adicionar
  Alert,
  Chip, // Para status
  Dialog, // Para confirmação
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions as DialogActionsMUI, // Renomear para evitar conflito
  Grid, // Para layout dos filtros E cards
  TextField, // Para filtro de texto
  Select, // Para filtro de status
  MenuItem, // Para filtro de status
  FormControl, // Para filtro de status
  InputLabel, // Para filtro de status
  Stack, // Para organizar botões
  TablePagination, // Manter para paginação
  Tooltip, // <<< Adicionar import do Tooltip
  IconButton // <<< Adicionar IconButton
} from '@mui/material';
// Remover imports específicos do DataGrid
// import { DataGrid, GridColDef, GridRenderCellParams, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { GridPaginationModel } from '@mui/x-data-grid'; // Manter apenas GridPaginationModel
import { useNavigate } from 'react-router-dom';
import { Edit, Eye, Search, RotateCcw, FilePlus, HardDrive, PauseCircle, CalendarDays, Clock, EyeOff } from 'lucide-react'; // Ajustar imports de ícones
import { Contract } from '../../types/contract'; // Importa tipo
import { useTheme, alpha } from '@mui/material/styles';
import ContractService from '../../services/contractService'; // <<< IMPORTAR SERVIÇO
import { formatDateToBR } from '../../utils/dateUtils';

// Objeto de tradução (pode ser mantido se TablePagination usar)
const ptBRLocaleText = {
  MuiTablePagination: {
    labelRowsPerPage: 'Linhas por página:',
    labelDisplayedRows: ({ from, to, count }: { from: number, to: number, count: number }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
  },
};

const ContractListPage = () => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Estado para o diálogo de exclusão
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contractToDelete, setContractToDelete] = useState<Contract | null>(null);

  // Estados para os filtros (inputs)
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<'Todos' | 'Ativo' | 'Encerrado'>('Todos');

  // Estado para os filtros APLICADOS que disparam a busca
  const [appliedFilters, setAppliedFilters] = useState({ 
    searchText: '', 
    status: 'Todos' 
  });

  // Estado para paginação
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 12
  });
  const [rowCount, setRowCount] = useState(0); // Total de itens no banco

  // Remover Estado para Ordenação
  // const [sortModel, setSortModel] = useState<GridSortModel>([
  //   { field: 'createdAt', sort: 'desc' },
  // ]);

  const navigate = useNavigate();
  const theme = useTheme();
  const contractService = ContractService; 

  // Efeito para buscar dados quando filtros aplicados ou paginação mudam
  useEffect(() => {
    fetchContracts();
  // Remover sortModel das dependências
  }, [contractService, appliedFilters, paginationModel]); 

  const fetchContracts = async () => {
    setLoading(true);
    setError(null);
    try {
      // Mapear estado para parâmetros da API
      const params: any = {
        page: paginationModel.page + 1, // API espera 1-based index
        limit: paginationModel.pageSize,
        searchText: appliedFilters.searchText,
        status: appliedFilters.status,
        // Remover parâmetros de ordenação
        // sortBy: sortModel.length > 0 ? sortModel[0].field : 'createdAt',
        // sortOrder: sortModel.length > 0 ? sortModel[0].sort : 'desc',
      };

      const response = await contractService.getAll(params); 
      setContracts(response.data);
      setRowCount(response.pagination.totalCount);
    } catch (err: any) { 
      const errorMessage = err?.response?.data?.message || err?.message || 'Falha ao carregar contratos.';
      setError(errorMessage);
      setContracts([]);
      setRowCount(0);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    navigate('/contracts/new');
  };

  const handleEdit = (id: number) => {
    navigate(`/contracts/edit/${id}`);
  };

  const handleView = (id: number) => {
    navigate(`/contracts/view/${id}`);
  };

  // Funções para lidar com a exclusão (mantidas)
  const handleDeleteClick = (contract: Contract) => {
    setContractToDelete(contract);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setContractToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!contractToDelete) return;
    console.log("Excluindo contrato (simulado):", contractToDelete.id);
    setContracts(prev => prev.filter(c => c.id !== contractToDelete.id));
    // Lógica real de exclusão comentada
    handleDeleteCancel();
  };

  // Handlers de filtro (mantidos)
  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleStatusFilterChange = (event: any) => { 
    setStatusFilter(event.target.value as 'Todos' | 'Ativo' | 'Encerrado');
  };

  const handleApplyFilters = () => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
    setAppliedFilters({ 
      searchText: searchText, 
      status: statusFilter 
    });
  };

  const handleClearFilters = () => {
    setSearchText('');
    setStatusFilter('Todos');
    setPaginationModel(prev => ({ ...prev, page: 0 })); 
    setAppliedFilters({ searchText: '', status: 'Todos' });
  };

  // Handler para paginação (ajustado para TablePagination)
  const handlePageChange = (event: unknown, newPage: number) => {
    setPaginationModel(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPaginationModel({ page: 0, pageSize: parseInt(event.target.value, 10) });
  };

  // Remover handleSortModelChange
  // const handleSortModelChange = (newModel: GridSortModel) => { ... };

  // Remover definição de colunas do DataGrid
  // const columns: GridColDef[] = [ ... ];

  // Função auxiliar para garantir a data de início correta
  const getStartDate = (contract: Contract) => {
    return contract.contract_start_date;
  };

  const [showValues, setShowValues] = useState(false); // <<< Novo estado para controlar visibilidade dos valores

  // Função para formatar valor ofuscado
  const formatOfuscatedValue = (value: number) => {
    return 'R$ ****,**';
  };

  return (
    <Box>
      {/* Cabeçalho com botão Novo Contrato e Mostrar/Ocultar valores */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" component="h1">
          Gestão de Contratos
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={showValues ? "Ocultar valores" : "Mostrar valores"}>
            <IconButton
              onClick={() => setShowValues(!showValues)}
              size="small"
              sx={{
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2),
                },
                height: 36,
                width: 36
              }}
            >
              {showValues ? <EyeOff size={18} /> : <Eye size={18} />}
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            color="primary"
            startIcon={<FilePlus size={16} />}
            onClick={handleAddNew}
            size="small"
            sx={{ 
              py: 0.5, 
              px: 2, 
              height: 36, 
              fontWeight: 'bold',
              fontSize: '0.75rem',
              borderRadius: 1
            }}
          >
            NOVO CONTRATO
          </Button>
        </Box>
      </Box>

      {/* Filtros (mantidos) */}
      <Box sx={{ 
          mb: 1.5, 
          p: 1,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: theme.shape.borderRadius
      }}>
        <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={6} md={5}>
                <TextField
                    fullWidth
                    label="Buscar por Empresa ou Pessoa"
                    variant="outlined"
                    value={searchText}
                    onChange={handleSearchTextChange}
                    size="small"
                    margin="dense"
                    InputProps={{ 
                        style: { fontSize: '0.85rem' },
                        sx: { py: 0.3, height: 36 },
                        startAdornment: <Search size={16} style={{ marginRight: 8, color: '#666' }} />,
                    }}
                    InputLabelProps={{ 
                        style: { fontSize: '0.85rem' } 
                    }}
                    placeholder="Buscar por Empresa ou Pessoa"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1 } }}
                />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small" margin="dense">
                    <InputLabel id="status-filter-label" sx={{ fontSize: '0.85rem' }}>Status</InputLabel>
                    <Select
                        labelId="status-filter-label"
                        value={statusFilter}
                        label="Status"
                        onChange={handleStatusFilterChange}
                        sx={{ 
                            fontSize: '0.85rem',
                            '.MuiSelect-select': { py: 0.5 },
                            height: 36
                        }}
                    >
                        <MenuItem value="Todos" sx={{ fontSize: '0.85rem' }}>Todos</MenuItem>
                        <MenuItem value="Ativo" sx={{ fontSize: '0.85rem' }}>Ativo</MenuItem>
                        <MenuItem value="Encerrado" sx={{ fontSize: '0.85rem' }}>Encerrado</MenuItem>
                    </Select>
                </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
                <Stack direction="row" spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                    <Button
                        variant="contained"
                        onClick={handleApplyFilters}
                        size="small"
                        sx={{ 
                            py: 0.5, 
                            px: 2,
                            height: 36,
                            fontWeight: 'bold', 
                            fontSize: '0.75rem',
                            borderRadius: 1
                        }}
                        startIcon={<Search size={16} />}
                    >
                        BUSCAR
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleClearFilters}
                        size="small"
                        sx={{ 
                            py: 0.5, 
                            px: 2,
                            height: 36,
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            borderRadius: 1
                        }}
                        startIcon={<RotateCcw size={16} />}
                    >
                        LIMPAR
                    </Button>
                </Stack>
            </Grid>
        </Grid>
      </Box>

      {/* Alerta de erro (mantido) */}
      {error && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0, fontSize: '0.875rem' }}>
          {error}
        </Alert>
      )}

      {/* Indicador de Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Grid de Cards - Substitui o DataGrid */}
      {!loading && contracts.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {contracts.map((contract) => {
            // <<< Definir cor condicional >>>
            const cardColor = contract.active ? theme.palette.primary.main : theme.palette.error.main;
            
            return (
              <Grid item key={contract.id} xs={12} sm={6} md={4} lg={3}>
                <Card 
                  elevation={2} 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    height: '100%', // Garante que os cards tenham altura igual na linha
                    borderLeft: `3px solid ${cardColor}`,
                    position: 'relative' // <<< Adicionar position relative para o posicionamento absoluto do Chip
                  }}
                >
                  <CardContent sx={{ flexGrow: 1, p: 1.5, pb: 1 /* Reduzir padding bottom para compensar actions */ }}>
                    {/* <<< RECOLOCAR O CHIP DE STATUS COM POSICIONAMENTO ABSOLUTO E NOVO ESTILO >>> */}
                    <Chip
                      label={contract.active ? 'Ativo' : 'Encerrado'}
                      color={contract.active ? 'success' : 'error'} // <<< Usar 'error' para Encerrado
                      size="small"
                      // variant="outlined" // <<< Remover variant outlined
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        fontSize: '0.7rem',
                        height: '20px',
                        fontWeight: 'bold', // <<< Manter negrito ou adicionar se necessário
                        color: 'white', // <<< Definir cor da fonte como branca
                        '& .MuiChip-label': {
                          padding: '0px 6px'
                        }
                      }}
                    />
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ fontSize: '0.7rem', mt: 0 /* Remover margem superior */ }}>
                      Contrato #{contract.id}
                    </Typography>
                    {/* Nome da Empresa com Tooltip - CORRIGIDO */}
                    <Tooltip title={contract.empresaNome || 'Empresa não informada'} arrow>
                      <Typography 
                        variant="subtitle2" 
                        component="div" 
                        sx={{
                          fontSize: '0.9rem',
                          lineHeight: 1.3,
                          mb: 0.5,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'block'
                        }}
                      >
                        {contract.empresaNome || 'Empresa não informada'}
                      </Typography>
                    </Tooltip>
                    {/* Nome da Pessoa com Tooltip - CORRIGIDO */}
                    <Tooltip title={contract.pessoaNome || 'Pessoa não informada'} arrow>
                      <Typography 
                        sx={{
                          fontSize: '0.75rem',
                          color: 'text.secondary',
                          mb: 1,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: 'block'
                        }}
                      >
                        {contract.pessoaNome || 'Pessoa não informada'}
                      </Typography>
                    </Tooltip>
                    {/* Exibição da Data Condicional (Início ou Período) */}
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                      {contract.active ? 
                        `Início do contrato: ${formatDateToBR(getStartDate(contract))}` :
                        `Período: ${formatDateToBR(getStartDate(contract))} a ${formatDateToBR(contract.contract_termination_date ?? undefined)}`
                      }
                    </Typography>
                    {/* <<< ADICIONAR EXIBIÇÃO DO TIPO PAGAMENTO E VALOR ATUAL >>> */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 0.5, mb: 0.5 }}>
                      {/* <<< Ícone Condicional para Tipo de Pagamento e Valor ao lado >>> */}
                      <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                        {contract.currentPaymentTypeName === 'Fixo Mensal' ? (
                          <Tooltip title="Tipo de Pagamento: Fixo Mensal">
                            <CalendarDays size={14} style={{ marginRight: 4 }} />
                          </Tooltip>
                        ) : contract.currentPaymentTypeName === 'Por Hora' ? (
                          <Tooltip title="Tipo de Pagamento: Por Hora">
                            <Clock size={14} style={{ marginRight: 4 }} />
                          </Tooltip>
                        ) : (
                          <Typography variant="inherit" sx={{ mr: 0.5 }}> {/* Adiciona margem se for texto */}
                            {contract.currentPaymentTypeName || '-'}
                          </Typography>
                        )}
                        {/* Valor do Contrato - Ajustar estilo */}
                        <Typography variant="body2" sx={{
                          fontSize: '0.9rem',
                          fontWeight: 'bold',
                          color: showValues ? 'text.primary' : 'text.secondary', // Cor cinza quando ofuscado
                          fontFamily: showValues ? 'inherit' : 'monospace', // Usar fonte monospace para o valor ofuscado
                          letterSpacing: showValues ? 'normal' : '2px' // Espaçamento maior entre os asteriscos
                        }}>
                          {contract.currentValue !== undefined
                            ? showValues 
                              ? contract.currentValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
                              : formatOfuscatedValue(contract.currentValue)
                            : '-'}
                        </Typography>
                      </Box>
                    </Box>
                    {/* Mostrar ícones e contagens com Tooltips */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 1.5, mt: 1 }}>
                      <Tooltip title={`Aditivos Contratuais: ${contract._count?.contractAdditives ?? 0}`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', cursor: 'default' /* Indicar que é informativo */ }}>
                          <FilePlus size={14} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>
                            {contract._count?.contractAdditives ?? 0}
                          </Typography>
                        </Box>
                      </Tooltip>
                      <Tooltip title={`Equipamentos Alocados: ${contract._count?.contractEquipments ?? 0}`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', cursor: 'default' }}>
                          <HardDrive size={14} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>
                            {contract._count?.contractEquipments ?? 0}
                          </Typography>
                        </Box>
                      </Tooltip>
                      <Tooltip title={`Suspensões Registradas: ${contract._count?.contractSuspendeds ?? 0}`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary', cursor: 'default' }}>
                          <PauseCircle size={14} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', lineHeight: 1 }}>
                            {contract._count?.contractSuspendeds ?? 0}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ justifyContent: 'flex-end', p: 0.5, borderTop: `1px solid ${theme.palette.divider}` }}>
                    <Button 
                        size="small"
                        onClick={() => handleView(contract.id)}
                        title="Visualizar Contrato"
                        sx={{ minWidth: 'auto', p: '4px' }} 
                    >
                        <Eye size={16} />
                    </Button>
                    <Button
                        size="small"
                        onClick={() => handleEdit(contract.id)}
                        title="Editar Contrato"
                        sx={{ minWidth: 'auto', p: '4px' }} 
                    >
                        <Edit size={16} />
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Mensagem se não houver contratos e não estiver carregando */}
      {!loading && contracts.length === 0 && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
          <Typography color="text.secondary">Nenhum contrato encontrado.</Typography>
        </Box>
      )}

      {/* Paginação (mantida, usando TablePagination) */}
      {!loading && rowCount > 0 && (
        <TablePagination
          component="div"
          count={rowCount}
          page={paginationModel.page}
          onPageChange={handlePageChange} 
          rowsPerPage={paginationModel.pageSize}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[12, 24, 36, 48]}
          labelRowsPerPage="Itens por página:"
          labelDisplayedRows={ptBRLocaleText.MuiTablePagination.labelDisplayedRows}
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            mt: 2,
            // Estilos menores para paginação
            '.MuiTablePagination-toolbar': {
              minHeight: '40px' 
            },
            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows, .MuiTablePagination-select, .MuiTablePagination-selectIcon': {
              fontSize: '0.8rem',
            },
            '.MuiTablePagination-actions button': {
              padding: '4px'
            }
          }}
        />
      )}

      {/* Diálogo de exclusão (mantido) */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        PaperProps={{
          sx: {
            width: '100%'
          }
        }}
      >
        <DialogTitle sx={{ fontSize: '1rem', pt: 1.5, pb: 1 }}>Confirmar Exclusão</DialogTitle>
        <DialogContent sx={{ py: 1 }}>
          <DialogContentText sx={{ fontSize: '0.9rem' }}>
            Tem certeza que deseja excluir o contrato entre {contractToDelete?.empresaNome} e {contractToDelete?.pessoaNome}?
            Esta ação não pode ser revertida.
          </DialogContentText>
        </DialogContent>
        {/* Usar DialogActionsMUI renomeado */}
        <DialogActionsMUI sx={{ px: 2, pb: 1.5 }}>
          <Button onClick={handleDeleteCancel} size="small">Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus size="small">
            Excluir
          </Button>
        </DialogActionsMUI>
      </Dialog>
    </Box>
  );
};

export default ContractListPage;